/* gfx.js — small helpers that let us transcribe the original SVG almost verbatim.
   Everything here is deliberately dumb: SVG path strings in, Pixi display objects out. */

import { Container, Graphics, GraphicsPath, Sprite, Texture, Text, TextStyle } from 'pixi.js';

/* ---------- paths ---------- */

export function P(d) {
  return new GraphicsPath(d);
}

/** fill one or more SVG subpaths with a flat colour */
export function fillPath(g, d, color, alpha) {
  g.path(P(d)).fill({ color, alpha: alpha == null ? 1 : alpha });
  return g;
}

/** stroke one or more SVG subpaths */
export function strokePath(g, d, opts) {
  g.path(P(d)).stroke(Object.assign({ width: 1, color: 0xffffff, alpha: 1 }, opts));
  return g;
}

/* ---------- gradient textures ---------------------------------------------
   SVG gradients here all use objectBoundingBox units, which means "map the
   unit square onto the shape's bbox". So: bake the gradient into a square
   texture, then stretch a Sprite over the bbox. Same maths, one quad.        */

const _texCache = new Map();

function stopsToCanvasGrad(grad, stops) {
  for (const [off, col, a] of stops) grad.addColorStop(off, rgba(col, a));
  return grad;
}

function rgba(hex, a) {
  const r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  return `rgba(${r},${g},${b},${a == null ? 1 : a})`;
}

const GRAD_SZ = 256;

/** stops: [[offset, 0xRRGGBB, alpha?], ...] */
export function linearGradTex(key, stops, horizontal) {
  const k = 'L' + key;
  if (_texCache.has(k)) return _texCache.get(k);
  const c = document.createElement('canvas');
  c.width = horizontal ? GRAD_SZ : 4;
  c.height = horizontal ? 4 : GRAD_SZ;
  const ctx = c.getContext('2d');
  const g = horizontal
    ? ctx.createLinearGradient(0, 0, c.width, 0)
    : ctx.createLinearGradient(0, 0, 0, c.height);
  stopsToCanvasGrad(g, stops);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, c.width, c.height);
  const t = Texture.from(c);
  _texCache.set(k, t);
  return t;
}

/** cx/cy/r are fractions of the bbox (SVG objectBoundingBox radialGradient) */
export function radialGradTex(key, stops, cx = 0.5, cy = 0.5, r = 0.5) {
  const k = 'R' + key;
  if (_texCache.has(k)) return _texCache.get(k);
  const c = document.createElement('canvas');
  c.width = c.height = GRAD_SZ;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(cx * GRAD_SZ, cy * GRAD_SZ, 0, cx * GRAD_SZ, cy * GRAD_SZ, r * GRAD_SZ);
  stopsToCanvasGrad(g, stops);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, GRAD_SZ, GRAD_SZ);
  const t = Texture.from(c);
  _texCache.set(k, t);
  return t;
}

/** a soft-edged ring, used for ripples and rain splashes */
export function ringTex() {
  const k = 'ring';
  if (_texCache.has(k)) return _texCache.get(k);
  const S = 128;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.arc(S / 2, S / 2, S / 2 - 2, 0, Math.PI * 2);
  ctx.stroke();
  const t = Texture.from(c);
  _texCache.set(k, t);
  return t;
}

/** a vertical rain streak with round caps */
export function streakTex() {
  const k = 'streak';
  if (_texCache.has(k)) return _texCache.get(k);
  const W = 4, H = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  ctx.strokeStyle = '#ffffff';
  ctx.lineCap = 'round';
  ctx.lineWidth = W - 1;
  ctx.beginPath();
  ctx.moveTo(W / 2, W / 2);
  ctx.lineTo(W / 2, H - W / 2);
  ctx.stroke();
  const t = Texture.from(c);
  _texCache.set(k, t);
  return t;
}

/** film grain, tiled */
export function noiseTex(size = 128) {
  const k = 'noise' + size;
  if (_texCache.has(k)) return _texCache.get(k);
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = (Math.random() * 255) | 0;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const t = Texture.from(c);
  _texCache.set(k, t);
  return t;
}

/* ---------- gradient-filled shapes ---------- */

/** rect filled with a gradient texture */
export function gradRect(tex, x, y, w, h, alpha) {
  const s = new Sprite(tex);
  s.x = x; s.y = y; s.width = w; s.height = h;
  if (alpha != null) s.alpha = alpha;
  return s;
}

/** arbitrary SVG path filled with a gradient: sprite stretched over the path
    bbox, masked by the path itself. Cheap, and exact for straight-edged shapes. */
export function gradPath(tex, d, alpha) {
  const c = new Container();
  const shape = new Graphics().path(P(d)).fill(0xffffff);
  const b = shape.getLocalBounds();
  const s = new Sprite(tex);
  s.x = b.x; s.y = b.y; s.width = b.width; s.height = b.height;
  if (alpha != null) s.alpha = alpha;
  c.addChild(s, shape);
  s.mask = shape;
  return c;
}

/* ---------- text -----------------------------------------------------------
   Rendered at 4x and scaled down, so it stays crisp at any camera scale
   without ever having to re-rasterise on resize.                             */

const SS = 4;
export const SERIF = 'Hiragino Mincho ProN, Yu Mincho, Noto Serif JP, Noto Serif CJK JP, YuMincho, MS PMincho, serif';
export const SANS = 'Hiragino Kaku Gothic ProN, Hiragino Sans, Noto Sans JP, Yu Gothic, Meiryo, system-ui, sans-serif';

/** `y` is the SVG baseline; anchor mimics text-anchor="middle" */
export function svgText(str, x, y, size, color, opts = {}) {
  const t = new Text({
    text: str,
    style: new TextStyle({
      fontFamily: opts.family || SERIF,
      fontSize: size * SS,
      fill: color,
      letterSpacing: (opts.letterSpacing || 0) * SS,
      fontWeight: opts.weight || 'normal',
      align: 'center',
    }),
  });
  t.scale.set(1 / SS);
  t.anchor.set(opts.anchor == null ? 0.5 : opts.anchor, 0);
  t.x = x;
  t.y = y - size * 0.82; // baseline -> top of em box (serif ascent approximation)
  if (opts.alpha != null) t.alpha = opts.alpha;
  t.resolution = 1;
  return t;
}

/* ---------- easing / keyframes ---------- */

export function easeInOut(t) {
  return 0.5 - 0.5 * Math.cos(Math.PI * t);
}

export function easeOut(t) {
  return 1 - (1 - t) * (1 - t);
}

/** CSS-ish keyframe sampler. frames: [[pos0..1, value], ...], eased in-out. */
export function kf(t, frames) {
  for (let i = 1; i < frames.length; i++) {
    if (t <= frames[i][0]) {
      const a = frames[i - 1], b = frames[i];
      const span = b[0] - a[0] || 1;
      return a[1] + (b[1] - a[1]) * easeInOut((t - a[0]) / span);
    }
  }
  return frames[frames.length - 1][1];
}

/** a container that rotates/scales about an arbitrary point in parent space */
export function pivotAt(x, y) {
  const outer = new Container();
  outer.position.set(x, y);
  const inner = new Container();
  inner.position.set(-x, -y);
  outer.addChild(inner);
  outer.inner = inner;
  return outer;
}
